/**
 * Composants de surface : tiroir, modale, toast, infobulle.
 *
 * Ils appartiennent aux couches 3 à 5 du §3 : panneaux, célébrations, modales.
 * Aucun n'apparaît ni ne disparaît sèchement — c'est la règle du §2, et c'est aussi
 * ce qui fait qu'un tiroir donne envie d'être rouvert.
 */

import { DURATION } from '../tokens';
import { icon } from '../icons';
import type { IconName } from '../icons';
import type { Component } from './index';

/* ── Tiroir (bottom sheet) ──────────────────────────────────────────────── */

/**
 * Positions du tiroir, en fraction de sa hauteur masquée : fermé, demi, plein.
 * Trois crans et pas davantage — un tiroir à positions libres oblige le joueur à viser.
 *
 * ⚠️ **Ces fractions se lisent avec `height` de la bande, jamais seules.** En mise en
 * page compacte, `.v-shell--compact .v-shell__sheet` fait 27 % de l'écran et son bord bas
 * est calé au haut du dock. La place occupée vaut donc `27 % × (1 − fraction)` :
 *
 * | cran | fraction masquée | écran occupé |
 * | ---- | ---------------- | ------------ |
 * | 0 — fermé | **1** | 0 % — la bande sort de l'écran, le dock suffit |
 * | 2 — ouvert (défaut du dock) | 0 | **27 %**, et rien n'est coupé |
 * | 1 — intermédiaire | 0,4 | ~16 %, accessible au glissement |
 *
 * **Le cran fermé masque TOUT, sans laisser de poignée.** Il en laissait 6 %, qui
 * tombaient exactement derrière le dock : on voyait un titre « Construire » coupé en deux
 * par la barre de navigation — une poignée invisible ne sert personne, et un texte
 * tronqué se lit comme un bug. Le dock EST l'affordance : ses quatre entrées ouvrent le
 * tiroir, c'est plus visible et plus grand qu'une poignée de 4 px.
 *
 * Avant resserrage : hauteur 78 % et crans [0,62 · 0,28 · 0], soit **30 / 56 / 78 %**
 * d'écran. Le cran par défaut mangeait donc plus de la moitié de la ville, et c'est la
 * cause directe du défaut consigné en règle 18 — « le tiroir qui masque ce qu'on vient
 * de poser ». On choisit un bâtiment dans une bande, pas dans un panneau.
 */
export const SHEET_SNAPS = [1, 0.4, 0] as const;

export type SheetPosition = 0 | 1 | 2;

/**
 * Cran le plus proche d'une fraction donnée. Fonction pure : c'est la seule partie
 * du glissement qui mérite un test, et celle qui décide du ressenti au relâchement.
 */
export function nearestSnap(fraction: number, snaps: readonly number[] = SHEET_SNAPS): number {
  let best = 0;
  for (let i = 1; i < snaps.length; i++) {
    const candidate = snaps[i];
    const current = snaps[best];
    if (candidate === undefined || current === undefined) continue;
    if (Math.abs(candidate - fraction) < Math.abs(current - fraction)) {
      best = i;
    }
  }
  return best;
}

export interface SheetOptions {
  readonly title: string;
  readonly hint?: string;
  readonly position?: SheetPosition;
  readonly onPositionChange?: (position: SheetPosition) => void;
}

export interface SheetHandle extends Component {
  readonly body: HTMLElement;
  setPosition(position: SheetPosition, animate?: boolean): void;
  getPosition(): SheetPosition;
  /**
   * Change le titre affiché ET l'étiquette d'accessibilité.
   *
   * Le tiroir est UNIQUE et sert les quatre sections du dock : son titre était figé sur
   * « Construire » à la création, si bien qu'ouvrir « Social » affichait le partage sous
   * l'en-tête « Construire ». Les deux `aria-label` suivent, sinon un lecteur d'écran
   * annonce toujours la première section.
   */
  setTitle(title: string, hint?: string): void;
  destroy(): void;
}

export function createSheet(options: SheetOptions): SheetHandle {
  const el = document.createElement('section');
  el.className = 'v-sheet';
  el.setAttribute('aria-label', options.title);

  const grip = document.createElement('div');
  grip.className = 'v-sheet__grip';
  grip.setAttribute('role', 'button');
  grip.setAttribute('tabindex', '0');
  grip.setAttribute('aria-label', options.title);

  const head = document.createElement('div');
  head.className = 'v-sheet__head';
  const heading = document.createElement('h3');
  heading.textContent = options.title;
  head.append(heading);

  const hint = document.createElement('span');
  hint.className = 'v-sheet__hint';
  hint.hidden = options.hint === undefined;
  if (options.hint !== undefined) hint.textContent = options.hint;
  head.append(hint);

  const body = document.createElement('div');
  body.className = 'v-sheet__body';

  el.append(grip, head, body);

  let position: SheetPosition = options.position ?? 1;
  let dragging = false;
  let moved = false;
  let startY = 0;
  let startFraction = 0;

  const apply = (fraction: number, animate: boolean): void => {
    el.classList.toggle('is-animating', animate);
    el.style.transform = `translateY(${(fraction * 100).toFixed(3)}%)`;
  };

  /**
   * Marque le cran fermé.
   *
   * Défini UNE fois et appelé aux deux endroits qui posent un cran — la construction et
   * `setPosition`. La construction appliquait sa translation sans passer par
   * `setPosition` : la classe n'était donc jamais posée au démarrage, et le tiroir
   * restait peint au repos alors que le code de fermeture, lui, était juste.
   */
  const markClosed = (next: SheetPosition): void => {
    el.classList.toggle('is-closed', next === 0);
  };

  const setPosition: SheetHandle['setPosition'] = (next, animate = true) => {
    position = next;
    /*
      Le cran FERMÉ se marque, et pas seulement se translate.

      Mesuré en production par balayage de `elementFromPoint` : au cran fermé, le tiroir
      restait PEINT sur 601 points de l'écran. Sa boîte est calée 88 px au-dessus du bord
      (au haut du dock), si bien qu'une translation de 100 % de sa propre hauteur laisse
      justement ces 88 px dans le viewport — invisibles derrière le dock, sauf autour de
      ses coins arrondis, où un liseré sombre dépassait.

      Une sonde géométrique ne peut PAS voir ce défaut : `getBoundingClientRect` ignore
      ce qu'un autre calque recouvre. C'est la mesure par peinture qui l'a trouvé.

      `visibility` plutôt qu'un décalage de translation : ça règle le liseré ET retire le
      tiroir fermé du parcours de tabulation, ce qu'une simple translation ne fait pas.
      La bascule est retardée d'une durée de panneau (voir `components.css`) pour que le
      glissement de sortie reste visible — D§2 interdit tout changement d'état sec.
    */
    markClosed(next);
    apply(SHEET_SNAPS[next], animate);
    options.onPositionChange?.(next);
  };

  markClosed(position);
  apply(SHEET_SNAPS[position], false);

  const height = (): number => Math.max(1, el.getBoundingClientRect().height);

  const onDown = (event: PointerEvent): void => {
    dragging = true;
    moved = false;
    startY = event.clientY;
    startFraction = SHEET_SNAPS[position];
    // Le doigt reprend la main : le tiroir redevient visible avant même d'avoir bougé,
    // sinon on glisserait un élément que le navigateur ne peint pas.
    el.classList.remove('is-closed');
    el.classList.remove('is-animating');
    grip.setPointerCapture(event.pointerId);
  };

  const onMove = (event: PointerEvent): void => {
    if (!dragging) return;
    if (Math.abs(event.clientY - startY) > 3) moved = true;
    const fraction = Math.min(
      // Jusqu'au cran le plus masqué, sinon le geste ne peut pas fermer le tiroir : le
      // plafond valait 0,72 quand le cran fermé en demandait 1, et glisser vers le bas
      // butait donc à mi-course.
      SHEET_SNAPS[0],
      Math.max(0, startFraction + (event.clientY - startY) / height()),
    );
    apply(fraction, false);
  };

  const onUp = (event: PointerEvent): void => {
    if (!dragging) return;
    dragging = false;
    if (grip.hasPointerCapture(event.pointerId)) {
      grip.releasePointerCapture(event.pointerId);
    }
    const fraction = Math.min(
      // Jusqu'au cran le plus masqué, sinon le geste ne peut pas fermer le tiroir : le
      // plafond valait 0,72 quand le cran fermé en demandait 1, et glisser vers le bas
      // butait donc à mi-course.
      SHEET_SNAPS[0],
      Math.max(0, startFraction + (event.clientY - startY) / height()),
    );
    setPosition(nearestSnap(fraction) as SheetPosition);
  };

  // Un simple tap fait défiler les trois crans : le geste au pouce reste possible
  // même sans glisser.
  const cycle = (): void => {
    if (moved) return;
    setPosition(((position + 1) % SHEET_SNAPS.length) as SheetPosition);
  };

  grip.addEventListener('pointerdown', onDown);
  grip.addEventListener('pointermove', onMove);
  grip.addEventListener('pointerup', onUp);
  grip.addEventListener('pointercancel', onUp);
  grip.addEventListener('click', cycle);
  grip.addEventListener('keydown', (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      moved = false;
      cycle();
    }
  });

  return {
    el,
    body,
    setPosition,
    getPosition: () => position,
    setTitle(title, nextHint) {
      heading.textContent = title;
      el.setAttribute('aria-label', title);
      grip.setAttribute('aria-label', title);
      hint.hidden = nextHint === undefined;
      if (nextHint !== undefined) hint.textContent = nextHint;
    },
    destroy() {
      grip.removeEventListener('pointerdown', onDown);
      grip.removeEventListener('pointermove', onMove);
      grip.removeEventListener('pointerup', onUp);
      grip.removeEventListener('pointercancel', onUp);
      grip.removeEventListener('click', cycle);
    },
  };
}

/* ── Modale ─────────────────────────────────────────────────────────────── */

export interface ModalOptions {
  readonly title: string;
  readonly text?: string;
  readonly icon?: IconName;
  /** Réservée aux décisions engageantes (D§3). Rares par principe. */
  readonly onClose?: () => void;
}

export interface ModalHandle extends Component {
  readonly content: HTMLElement;
  readonly actions: HTMLElement;
  open(): void;
  close(): void;
  isOpen(): boolean;
  destroy(): void;
}

/**
 * Ce qui peut réellement recevoir le focus dans une modale.
 *
 * ── `a[href]` et surtout PAS `[href]` ───────────────────────────────────────
 *
 * Le sélecteur disait `[href]`, qui capture aussi les **`<use href>` des icônes SVG**.
 * Chaque modale portant une icône commence donc par un `<use>` — le crest, tout en haut
 * du dialogue — et c'est lui que `open()` tentait de focaliser. Un `<use>` n'est pas
 * focalisable : l'appel échoue **en silence**, le focus reste où il était, et le piège à
 * focus prend le même mauvais premier élément.
 *
 * Conséquence mesurée, et elle n'est pas cosmétique : la confirmation d'effacement de la
 * ville s'ouvrait avec le focus resté sur « Effacer ma ville », le bouton qui l'avait
 * déclenchée. Une touche « Entrée » réflexe validait donc la destruction — alors que
 * l'ordre des boutons avait précisément été choisi pour que ce soit « Annuler » qui
 * reçoive le focus.
 *
 * Le défaut existait dans TOUTES les modales à icône : réglages, boutique de gemmes,
 * journal de retour, confirmations. Il ne se voyait nulle part parce qu'échouer à
 * focaliser ne lève rien.
 */
const FOCUSABLE =
  'button:not([disabled]), a[href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

/** Les deux boutons d'une décision engageante. */
export interface DecisionButtons {
  /** Celui qui ne coûte rien. Reçoit le focus à l'ouverture, par construction. */
  readonly cancel: HTMLElement;
  /** Celui qui engage. Vendre, effacer, prestiger. */
  readonly confirm: HTMLElement;
}

/**
 * Pose les deux boutons d'une décision engageante — dans le seul ordre acceptable.
 *
 * ── Pourquoi une fonction pour deux `append` ────────────────────────────────
 *
 * Parce que l'invariant « Annuler passe EN PREMIER » n'existait que sous forme de
 * **commentaire**, recopié dans `confirm.ts` et dans `preferences.ts`. Un commentaire
 * n'a jamais empêché personne d'inverser deux lignes, et l'enjeu n'est pas cosmétique :
 * `createModal` focalise le premier élément focalisable, donc l'ordre décide de ce qu'un
 * « Entrée » réflexe déclenche. Sur la remise à zéro, c'est une ville de plusieurs
 * heures. L'ordre n'est plus un choix laissé à l'appelant — il n'y a plus d'appelant qui
 * puisse se tromper.
 *
 * ── Et pourquoi `data-decision` ─────────────────────────────────────────────
 *
 * Pour que l'invariant soit **observable sans lire un libellé**. La sonde des réglages
 * vérifiait `focused.includes('Annuler')` : verte sur un poste français, **rouge sur le
 * runner anglais de la CI**, où le bouton s'appelle « Cancel » — le jeu, lui, était
 * parfaitement correct. Une sonde ne doit pas dépendre de la langue de la machine qui
 * l'exécute (règle 22 : on nomme ce qu'on vise).
 */
export function appendDecision(modal: ModalHandle, buttons: DecisionButtons): void {
  buttons.cancel.dataset.decision = 'cancel';
  buttons.confirm.dataset.decision = 'confirm';
  modal.actions.append(buttons.cancel, buttons.confirm);
}

export function createModal(options: ModalOptions): ModalHandle {
  const el = document.createElement('div');
  el.className = 'v-scrim';

  const dialog = document.createElement('div');
  dialog.className = 'v-modal';
  dialog.setAttribute('role', 'dialog');
  dialog.setAttribute('aria-modal', 'true');

  if (options.icon !== undefined) {
    const crest = document.createElement('div');
    crest.className = 'v-modal__crest';
    crest.append(icon(options.icon, { size: 'large' }));
    dialog.append(crest);
  }

  const heading = document.createElement('h3');
  heading.className = 'v-modal__title';
  heading.id = `v-modal-${Math.abs(hashString(options.title))}`;
  heading.textContent = options.title;
  dialog.setAttribute('aria-labelledby', heading.id);
  dialog.append(heading);

  if (options.text !== undefined) {
    const text = document.createElement('p');
    text.className = 'v-modal__text';
    text.textContent = options.text;
    dialog.append(text);
  }

  const content = document.createElement('div');
  content.className = 'v-modal__content';

  const actions = document.createElement('div');
  actions.className = 'v-modal__actions';

  dialog.append(content, actions);
  el.append(dialog);

  let open = false;
  let lastFocused: Element | null = null;

  const close = (): void => {
    if (!open) return;
    open = false;
    el.classList.remove('is-open');
    options.onClose?.();
    if (lastFocused instanceof HTMLElement) {
      lastFocused.focus();
    }
  };

  const onKeyDown = (event: KeyboardEvent): void => {
    if (!open) return;

    if (event.key === 'Escape') {
      close();
      return;
    }

    // Piège à focus : une modale qu'on peut quitter au clavier sans la fermer
    // laisse le joueur perdu derrière le voile.
    if (event.key === 'Tab') {
      const focusable = [...dialog.querySelectorAll<HTMLElement>(FOCUSABLE)];
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (first === undefined || last === undefined) return;

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  };

  el.addEventListener('click', (event) => {
    if (event.target === el) close();
  });
  document.addEventListener('keydown', onKeyDown);

  return {
    el,
    content,
    actions,
    open() {
      if (open) return;
      open = true;
      lastFocused = document.activeElement;
      el.classList.add('is-open');

      /*
        Le focus se pose à la FRAME SUIVANTE, jamais dans la même.

        Au repos, `.v-scrim` est en `visibility: hidden`. Ajouter `is-open` change la
        règle, mais le style n'est pas encore recalculé quand la ligne suivante s'exécute :
        `focus()` porte alors sur un élément que le navigateur considère toujours comme
        invisible, et **il échoue en silence** — aucune exception, aucun avertissement, le
        focus reste simplement où il était.

        Mesuré : la confirmation d'effacement de la ville s'ouvrait avec le focus resté
        sur « Effacer ma ville », le bouton qui venait de la déclencher. Une touche
        « Entrée » réflexe validait donc la destruction, alors que l'ordre des boutons
        avait précisément été choisi pour que « Annuler » reçoive le focus. Le même appel,
        rejoué 300 ms plus tard, fonctionnait parfaitement.

        Ce défaut existait dans toutes les modales depuis leur écriture. Il ne se voyait
        nulle part parce qu'échouer à focaliser ne lève rien.
      */
      requestAnimationFrame(() => {
        if (!open) return;
        dialog.querySelector<HTMLElement>(FOCUSABLE)?.focus();
      });
    },
    close,
    isOpen: () => open,
    destroy() {
      document.removeEventListener('keydown', onKeyDown);
    },
  };
}

function hashString(value: string): number {
  let hash = 0;
  for (let i = 0; i < value.length; i++) {
    hash = (hash * 31 + value.charCodeAt(i)) | 0;
  }
  return hash;
}

/* ── Toast ──────────────────────────────────────────────────────────────── */

export interface ToastOptions {
  readonly title: string;
  readonly text?: string;
  readonly icon?: IconName;
  readonly domain?: 'coins' | 'gems' | 'social' | 'prestige' | 'alert';
  readonly duration?: number;
}

export interface ToastHandle extends Component {
  show(options?: Partial<ToastOptions>): void;
  hide(): void;
}

/**
 * Couche de célébration : non interactive, s'efface seule (D§3).
 * `pointer-events: none` est essentiel — un toast qui intercepte un tap pendant que
 * le joueur construit est une frustration silencieuse.
 */
export function createToast(defaults: ToastOptions): ToastHandle {
  const el = document.createElement('div');
  el.className = 'v-toast';
  el.setAttribute('role', 'status');
  el.setAttribute('aria-live', 'polite');

  const crest = document.createElement('span');
  crest.className = 'v-toast__crest';

  const body = document.createElement('span');
  const title = document.createElement('b');
  const text = document.createElement('span');
  body.append(title, text);

  el.append(crest, body);

  let timer = 0;

  const paint = (options: ToastOptions): void => {
    title.textContent = options.title;
    text.textContent = options.text ?? '';
    crest.replaceChildren();
    if (options.icon !== undefined) {
      crest.append(icon(options.icon));
    }
    const domain = options.domain ?? 'gems';
    crest.style.background = `linear-gradient(180deg, var(--${domain}), var(--${domain}-deep))`;
    el.style.borderColor = `color-mix(in srgb, var(--${domain}) 40%, transparent)`;
  };

  paint(defaults);

  return {
    el,
    show(next = {}) {
      paint({ ...defaults, ...next });
      window.clearTimeout(timer);
      el.classList.remove('is-in');
      requestAnimationFrame(() => {
        el.classList.add('is-in');
        timer = window.setTimeout(
          () => {
            el.classList.remove('is-in');
          },
          next.duration ?? defaults.duration ?? 3200,
        );
      });
    },
    hide() {
      window.clearTimeout(timer);
      el.classList.remove('is-in');
    },
  };
}

/* ── Infobulle ──────────────────────────────────────────────────────────── */

export interface TooltipOptions {
  readonly text: string;
  readonly for: HTMLElement;
}

export function createTooltip(options: TooltipOptions): Component {
  const el = document.createElement('span');
  el.className = 'v-tooltip';
  el.setAttribute('role', 'tooltip');
  el.textContent = options.text;

  const show = (): void => {
    el.classList.add('is-in');
  };
  const hide = (): void => {
    el.classList.remove('is-in');
  };

  // Survol ET focus : une infobulle réservée à la souris n'existe pas au clavier
  // ni au doigt.
  options.for.addEventListener('pointerenter', show);
  options.for.addEventListener('pointerleave', hide);
  options.for.addEventListener('focus', show);
  options.for.addEventListener('blur', hide);

  return { el };
}

export { DURATION };
